import { useMemo } from 'react';
import styles from './unitSpecialPrices.module.css';

/*
 * "Special prices" on a unit's Pricing tab — unit.pricingRules.
 * Guests are charged these automatically: the camp page and the server
 * price each night with them (campBookingTab/stayRules.js, backend utils/stayRules.js).
 *
 *   weekend / weekday          -> nights of the chosen days
 *   season / holiday / festival / date range -> nights between two dates
 *   early bird / last minute   -> whole stay, by how far ahead it's booked
 * Price: "Set price" (that night costs ₹x) or "Change by %" (-10 = 10% off).
 */

const TYPES = [
    { value: 'weekend', label: 'Weekend', name: 'Weekend price' },
    { value: 'weekday', label: 'Weekday', name: 'Weekday price' },
    { value: 'season', label: 'Season', name: 'Season price' },
    { value: 'holiday', label: 'Holiday', name: 'Holiday price' },
    { value: 'festival', label: 'Festival', name: 'Festival price' },
    { value: 'date_range', label: 'Date range', name: 'Special dates price' },
    { value: 'early_bird', label: 'Early bird', name: 'Early bird offer' },
    { value: 'last_minute', label: 'Last minute', name: 'Last minute offer' }
];
const QUICK = [
    ['weekend', 'Weekend'],
    ['season', 'Season / holiday'],
    ['early_bird', 'Early bird'],
    ['last_minute', 'Last minute']
];
const DATE_TYPES = ['season', 'holiday', 'festival', 'date_range', 'special_event'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DEFAULT_DAYS = { weekend: [5, 6], weekday: [0, 1, 2, 3, 4] };

const num = (v) => (v === '' || v === null || v === undefined ? '' : Number(v));
const dateValue = (v) => (v ? String(v).slice(0, 10) : '');
const nameOf = (type) => TYPES.find((t) => t.value === type)?.name || 'Special price';

export const newRule = (type) => ({
    name: nameOf(type),
    type,
    days: DEFAULT_DAYS[type] ? [...DEFAULT_DAYS[type]] : [],
    startDate: '',
    endDate: '',
    daysBefore: type === 'early_bird' ? 30 : type === 'last_minute' ? 3 : '',
    adjustmentType: type === 'early_bird' || type === 'last_minute' ? 'percentage' : 'fixed',
    adjustmentValue: type === 'early_bird' || type === 'last_minute' ? -10 : '',
    minimumNights: 1,
    active: true
});

// what's wrong with a rule (shown under it; rules with problems are ignored when pricing)
export function ruleProblem(rule) {
    const value = Number(rule.adjustmentValue);
    if (rule.adjustmentType === 'fixed' && !(value > 0)) return 'Enter the price for these nights.';
    if (rule.adjustmentType !== 'fixed' && !value) return 'Enter the % change (e.g. 20 or -10).';
    if (rule.adjustmentType !== 'fixed' && value <= -100) return 'A discount must be less than 100%.';
    if (DATE_TYPES.includes(rule.type)) {
        if (!rule.startDate || !rule.endDate) return 'Pick the from and to dates.';
        if (dateValue(rule.endDate) < dateValue(rule.startDate)) return 'The "to" date is before the "from" date.';
    }
    if ((rule.type === 'weekend' || rule.type === 'weekday') && !(rule.days || []).length)
        return 'Pick at least one night.';
    if ((rule.type === 'early_bird' || rule.type === 'last_minute') && rule.daysBefore === '')
        return 'Enter the number of days.';
    return '';
}

export default function UnitSpecialPrices({ rules = [], onChange, basePrice = 0, canEdit = true }) {
    const list = useMemo(() => (Array.isArray(rules) ? rules : []), [rules]);
    const set = (index, patch) => onChange(list.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    const add = (type) => onChange([...list, newRule(type)]);
    const remove = (index) => onChange(list.filter((_, i) => i !== index));
    const base = Number(basePrice) || 0;
    const fmt = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;

    const preview = (rule) => {
        const value = Number(rule.adjustmentValue);
        if (!base || !value) return '';
        const price = rule.adjustmentType === 'fixed' ? value : Math.max(0, base * (1 + value / 100));
        if (rule.adjustmentType === 'fixed' && !(value > 0)) return '';
        return `${fmt(price)} a night instead of ${fmt(base)}`;
    };

    return (
        <section className={styles.wrap}>
            <div className={styles.head}>
                <div>
                    <h4>Special prices</h4>
                    <p>
                        Charge a different price on weekends, in a season or for early / last-minute bookings. Guests
                        are charged it automatically.
                    </p>
                </div>
            </div>

            {list.map((rule, index) => {
                const problem = ruleProblem(rule);
                const isDays = rule.type === 'weekend' || rule.type === 'weekday';
                const isDates = DATE_TYPES.includes(rule.type);
                const isBooking = rule.type === 'early_bird' || rule.type === 'last_minute';
                return (
                    <div
                        key={rule._id || index}
                        className={`${styles.rule} ${rule.active === false ? styles.off : ''}`}
                    >
                        <div className={styles.row}>
                            <select
                                value={rule.type}
                                disabled={!canEdit}
                                aria-label='Type'
                                onChange={(e) => {
                                    const type = e.target.value;
                                    const fresh = newRule(type);
                                    set(index, {
                                        type,
                                        name: rule.name === nameOf(rule.type) ? fresh.name : rule.name,
                                        days: fresh.days,
                                        daysBefore: fresh.daysBefore
                                    });
                                }}
                            >
                                {TYPES.map((t) => (
                                    <option key={t.value} value={t.value}>
                                        {t.label}
                                    </option>
                                ))}
                            </select>
                            <input
                                className={styles.name}
                                value={rule.name || ''}
                                disabled={!canEdit}
                                placeholder={nameOf(rule.type)}
                                aria-label='Name shown to guests'
                                onChange={(e) => set(index, { name: e.target.value })}
                            />
                            <label className={styles.toggle}>
                                <input
                                    type='checkbox'
                                    checked={rule.active !== false}
                                    disabled={!canEdit}
                                    onChange={(e) => set(index, { active: e.target.checked })}
                                />
                                On
                            </label>
                            {canEdit && (
                                <button
                                    type='button'
                                    className={styles.remove}
                                    onClick={() => remove(index)}
                                    aria-label='Remove'
                                >
                                    ×
                                </button>
                            )}
                        </div>

                        {isDays && (
                            <div className={styles.row}>
                                <span className={styles.label}>Nights of</span>
                                <div className={styles.days}>
                                    {DAYS.map((d, i) => {
                                        const on = (rule.days || []).map(Number).includes(i);
                                        return (
                                            <button
                                                type='button'
                                                key={d}
                                                disabled={!canEdit}
                                                aria-pressed={on}
                                                className={`${styles.day} ${on ? styles.dayOn : ''}`}
                                                onClick={() =>
                                                    set(index, {
                                                        days: on
                                                            ? (rule.days || []).filter((x) => Number(x) !== i)
                                                            : [...(rule.days || []), i].sort()
                                                    })
                                                }
                                            >
                                                {d}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {isDates && (
                            <div className={styles.row}>
                                <span className={styles.label}>From</span>
                                <input
                                    type='date'
                                    value={dateValue(rule.startDate)}
                                    disabled={!canEdit}
                                    onChange={(e) => set(index, { startDate: e.target.value })}
                                />
                                <span className={styles.label}>to</span>
                                <input
                                    type='date'
                                    value={dateValue(rule.endDate)}
                                    min={dateValue(rule.startDate) || undefined}
                                    disabled={!canEdit}
                                    onChange={(e) => set(index, { endDate: e.target.value })}
                                />
                                <span className={styles.hint}>nights from – to (both included)</span>
                            </div>
                        )}

                        {isBooking && (
                            <div className={styles.row}>
                                <span className={styles.label}>
                                    {rule.type === 'early_bird' ? 'Booked at least' : 'Booked within'}
                                </span>
                                <input
                                    type='number'
                                    min='0'
                                    className={styles.small}
                                    value={num(rule.daysBefore)}
                                    disabled={!canEdit}
                                    onChange={(e) => set(index, { daysBefore: num(e.target.value) })}
                                />
                                <span className={styles.label}>days before check-in</span>
                            </div>
                        )}

                        <div className={styles.row}>
                            <div className={styles.seg} role='group' aria-label='Price change'>
                                {[
                                    ['fixed', 'Set price'],
                                    ['percentage', 'Change by %']
                                ].map(([value, label]) => (
                                    <button
                                        type='button'
                                        key={value}
                                        disabled={!canEdit || (isBooking && value === 'fixed')}
                                        className={(rule.adjustmentType || 'percentage') === value ? styles.segOn : ''}
                                        onClick={() => set(index, { adjustmentType: value, adjustmentValue: '' })}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                            <div className={styles.money}>
                                {rule.adjustmentType === 'fixed' && <span>₹</span>}
                                <input
                                    type='number'
                                    value={num(rule.adjustmentValue)}
                                    disabled={!canEdit}
                                    placeholder={rule.adjustmentType === 'fixed' ? '8000' : '-10'}
                                    onChange={(e) => set(index, { adjustmentValue: num(e.target.value) })}
                                />
                                {rule.adjustmentType !== 'fixed' && <span>%</span>}
                            </div>
                            <span className={styles.label}>Min. nights</span>
                            <input
                                type='number'
                                min='1'
                                className={styles.small}
                                value={num(rule.minimumNights) || 1}
                                disabled={!canEdit}
                                onChange={(e) =>
                                    set(index, {
                                        minimumNights: Math.max(1, Number(e.target.value) || 1)
                                    })
                                }
                            />
                        </div>
                        {problem ? (
                            <p className={styles.problem}>{problem}</p>
                        ) : (
                            preview(rule) && <p className={styles.preview}>{preview(rule)}</p>
                        )}
                    </div>
                );
            })}

            {canEdit && (
                <div className={styles.quick}>
                    {QUICK.map(([type, label]) => (
                        <button type='button' key={type} onClick={() => add(type)}>
                            + {label}
                        </button>
                    ))}
                </div>
            )}
            {!canEdit && list.length === 0 && <p className={styles.hint}>No special prices.</p>}
        </section>
    );
}
